import { AuthenticationStep } from "@/components/integrations/AuthenticationStep";
import { FieldsStep } from "@/components/integrations/FieldsStep";
import {
  IntegrationSetupMode,
  IntegrationSetupProvider,
  IntegrationSetupStep,
  useIntegrationSetup,
} from "@/components/integrations/IntegrationSetupContext";
import { NotificationsStep } from "@/components/integrations/NotificationsStep";
import { SettingsStep } from "@/components/integrations/SettingsStep";
import {
  ProviderCapabilities,
  ProviderConfigurationMetadata,
  ProviderConnectionInfo,
} from "@/types/integrations";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { ReactNode, useEffect, useRef } from "react";

export interface IntegrationSetupWizardProps {
  /** Integration provider name (e.g., "hubspot", "salesforce") */
  integration: string;
  /** Setup or update mode */
  mode: IntegrationSetupMode;
  /** Optional prefix for session storage keys (e.g., "campaign-123") */
  storageKeyPrefix?: string;
  /** Called when the setup wizard completes successfully */
  onComplete?: (connectionInfo: ProviderConnectionInfo) => void;
  /** Called when the user cancels the wizard */
  onCancel?: () => void;
  /** Pre-loaded capabilities */
  capabilities?: ProviderCapabilities;
  /** Pre-loaded config metadata */
  configMetadata?: ProviderConfigurationMetadata[];
  /** Pre-loaded hasSettings flag */
  hasSettings?: boolean;
  /** Existing connection info to seed the wizard with (required for update mode) */
  connectionInfo?: ProviderConnectionInfo | null;
  /** User object for OAuth channel */
  user?: { lassoOrganizationId: string } | null;
  /** Custom success component */
  renderSuccess?: () => ReactNode;
  /** Auto-navigate delay after success (ms), set to 0 to disable */
  successAutoNavigateDelay?: number;
  /** Initial step to start the wizard on */
  initialStep?: IntegrationSetupStep;
  /** Called whenever the active step changes — useful for syncing the URL */
  onStepChange?: (step: IntegrationSetupStep) => void;
}

function WizardContent({
  user,
  renderSuccess,
  successAutoNavigateDelay = 2500,
  onStepChange,
}: Pick<
  IntegrationSetupWizardProps,
  "user" | "renderSuccess" | "successAutoNavigateDelay" | "onStepChange"
>) {
  const {
    currentStep,
    goToStep,
    goToNextStep,
    onComplete,
    onCancel,
    totalSteps,
    currentStepNumber,
    mode,
    hasSettings,
  } = useIntegrationSetup();

  // Keep a stable ref to onStepChange so the effect below only re-fires
  // when currentStep changes, not every time the parent re-renders and
  // passes a new inline function reference.
  const onStepChangeRef = useRef(onStepChange);
  useEffect(() => {
    onStepChangeRef.current = onStepChange;
  });

  // Notify the parent whenever the step changes
  useEffect(() => {
    onStepChangeRef.current?.(currentStep);
  }, [currentStep]);

  const getStepTitle = () => {
    switch (currentStep) {
      case "authentication":
        return "Forbind";
      case "fields":
        return "Opsætning af felter";
      case "settings":
        return "Indstillinger";
      case "notifications":
        return "Notifikationer";
      case "success":
        return "Færdig";
      default:
        return "";
    }
  };

  // Handle success auto-navigation
  useEffect(() => {
    if (currentStep !== "success" || successAutoNavigateDelay === 0) return;

    const timeoutId = window.setTimeout(() => {
      onComplete();
    }, successAutoNavigateDelay);

    return () => clearTimeout(timeoutId);
  }, [currentStep, successAutoNavigateDelay, onComplete]);

  const handleAuthNext = (updatedConnectionInfo?: ProviderConnectionInfo) => {
    if (mode === "update") {
      onComplete(updatedConnectionInfo);
    } else {
      goToNextStep();
    }
  };

  const handleFieldsNext = (updatedConnectionInfo?: ProviderConnectionInfo) => {
    if (mode === "update") {
      onComplete(updatedConnectionInfo);
    } else if (hasSettings) {
      goToStep("settings");
    } else {
      goToStep("success");
    }
  };

  const handleSettingsNext = (
    updatedConnectionInfo?: ProviderConnectionInfo,
  ) => {
    if (mode === "update") {
      onComplete(updatedConnectionInfo);
    } else {
      goToStep("success");
    }
  };

  const handleNotificationsComplete = (
    updatedConnectionInfo?: ProviderConnectionInfo,
  ) => {
    if (mode === "update") {
      onComplete(updatedConnectionInfo);
    } else {
      goToStep("success");
    }
  };

  const handleFieldsBack = () => {
    goToStep("authentication");
  };

  const handleSettingsBack = () => {
    goToStep("fields");
  };

  const handleNotificationsBack = () => {
    if (hasSettings) {
      goToStep("settings");
    } else {
      goToStep("fields");
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case "authentication":
        return (
          <AuthenticationStep
            onNext={handleAuthNext}
            onCancel={onCancel}
            user={user}
          />
        );
      case "fields":
        return (
          <FieldsStep onNext={handleFieldsNext} onBack={handleFieldsBack} />
        );
      case "settings":
        return (
          <SettingsStep
            onNext={handleSettingsNext}
            onBack={handleSettingsBack}
          />
        );
      case "notifications":
        return (
          <NotificationsStep
            onComplete={handleNotificationsComplete}
            onBack={handleNotificationsBack}
          />
        );
      case "success":
        if (renderSuccess) {
          return renderSuccess();
        }
        return <DefaultSuccessStep />;
      default:
        return null;
    }
  };

  return (
    <div className="text-theme-on-background mx-auto flex h-full w-full flex-col">
      {/* Progress indicator */}
      {currentStep !== "success" && (
        <div className="text-center">
          <div className="mb-4 flex items-center justify-center">
            {mode === "setup" && (
              <div className="text-theme-on-background-variant text-center text-xs">
                Trin {currentStepNumber} af {totalSteps}
              </div>
            )}
          </div>
          <p className="text-lg font-medium">{getStepTitle()}</p>
        </div>
      )}

      {/* Step content */}
      <div className="mt-14 flex-1">{renderStep()}</div>
    </div>
  );
}

function DefaultSuccessStep() {
  const { integration } = useIntegrationSetup();

  return (
    <div className="size-full">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, transition: { duration: 0.5, delay: 0.5 } }}
        className="flex size-full flex-col items-center justify-center gap-y-5"
      >
        <Check className="size-20 text-green-600 dark:text-green-400" />
        <p className="text-base font-medium">
          Du er forbundet til {integration}!
        </p>
      </motion.div>
    </div>
  );
}

/**
 * Self-contained integration setup wizard that can be embedded in any context.
 * Wraps the step components with IntegrationSetupProvider.
 */
export function IntegrationSetupWizard({
  integration,
  mode,
  storageKeyPrefix,
  onComplete,
  onCancel,
  capabilities,
  configMetadata,
  hasSettings,
  connectionInfo,
  user,
  renderSuccess,
  successAutoNavigateDelay,
  initialStep,
  onStepChange,
}: IntegrationSetupWizardProps) {
  return (
    <IntegrationSetupProvider
      integration={integration}
      mode={mode}
      storageKeyPrefix={storageKeyPrefix}
      onComplete={onComplete}
      onCancel={onCancel}
      capabilities={capabilities}
      configMetadata={configMetadata}
      hasSettings={hasSettings}
      connectionInfo={connectionInfo}
      initialStep={initialStep}
    >
      <WizardContent
        user={user}
        renderSuccess={renderSuccess}
        successAutoNavigateDelay={successAutoNavigateDelay}
        onStepChange={onStepChange}
      />
    </IntegrationSetupProvider>
  );
}
